<script>
  import { onMount } from 'svelte';
  import { Link } from 'svelte-routing';
  import { getApiUrl } from '../api';
  import { authFetch } from '../authService';
  import { t, currentLang } from '../i18nStore';
  import { notificationStore } from '../notificationStore';

  let promotions = [];
  let isLoading = true;
  let error = '';
  let successMessage = '';
  
  // Pagination
  let page = 1;
  const pageSize = 10;
  let totalPages = 1;
  let totalCount = 0;

  // Track promotions being unbanned to prevent duplicate clicks
  let unbanningIds = new Set();

  onMount(() => {
    fetchBannedPromotions();
  });

  async function fetchBannedPromotions() {
    isLoading = true;
    error = '';
    try {
      const res = await authFetch(getApiUrl(`/admin/promotions?banned=true&page=${page}&pageSize=${pageSize}&sortBy=createdAt`));
      if (!res.ok) {
        throw new Error($t('admin.banned_promos.unban_failed'));
      }
      
      const result = await res.json();
      promotions = result.data || [];
      totalCount = result.total || 0;
      totalPages = result.totalPages || 1;

      // Handle edge case where page becomes empty after unban
      if (promotions.length === 0 && page > 1) {
        page--;
        return fetchBannedPromotions();
      }
    } catch (e) {
      error = e.message || 'Failed to load banned promotions';
    } finally {
      isLoading = false;
    }
  }

  function formatLocalDateTime(promo) {
    let d = null;
    if (promo.createdAt) {
      d = new Date(promo.createdAt);
    } else if (promo._id) {
      try {
        const ts = parseInt(promo._id.substring(0, 8), 16) * 1000;
        if (!isNaN(ts)) d = new Date(ts);
      } catch (e) {}
    }

    if (!d || isNaN(d.getTime())) return 'N/A';

    const lang = $currentLang;
    let locale = 'en-US';
    if (lang === 'zh-HK') locale = 'zh-HK';
    if (lang === 'ja') locale = 'ja-JP';

    // Format in user's local timezone (timeZone omitted means local timezone)
    return d.toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  async function handleUnban(promo) {
    if (!confirm($t('admin.banned_promos.unban_confirm'))) {
      return;
    }

    unbanningIds.add(promo._id);
    unbanningIds = unbanningIds; // Trigger reactivity
    error = '';
    successMessage = '';

    try {
      const res = await authFetch(getApiUrl(`/admin/promotions/${promo._id}/unban`), {
        method: 'POST'
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || $t('admin.banned_promos.unban_failed'));
      }

      successMessage = $t('admin.banned_promos.unban_success');
      notificationStore.set({
        message: successMessage,
        type: 'info'
      });

      // Refetch list to refresh the pagination and count
      await fetchBannedPromotions();
    } catch (e) {
      error = e.message || $t('admin.banned_promos.unban_failed');
      notificationStore.set({
        message: error,
        type: 'error'
      });
    } finally {
      unbanningIds.delete(promo._id);
      unbanningIds = unbanningIds;
    }
  }

  function goToPage(newPage) {
    if (newPage >= 1 && newPage <= totalPages && newPage !== page) {
      page = newPage;
      fetchBannedPromotions();
    }
  }
</script>

<div class="admin-container">
  <div class="header-section">
    <h2>{$t('admin.banned_promos.title')}</h2>
    <p class="subtitle">{$t('admin.banned_promos.subtitle')}</p>
  </div>

  {#if successMessage}
    <div class="alert alert-success">
      {successMessage}
    </div>
  {/if}

  {#if error}
    <div class="alert alert-error">
      {error}
    </div>
  {/if}

  <div class="table-card">
    {#if isLoading}
      <div class="loading-state">
        <p>{$t('common.loading')}</p>
      </div>
    {:else if promotions.length === 0}
      <div class="empty-state">
        <p>{$t('admin.banned_promos.no_data')}</p>
      </div>
    {:else}
      <div class="table-responsive">
        <table>
          <thead>
            <tr>
              <th>{$t('admin.banned_promos.promo_name')}</th>
              <th>{$t('admin.banned_promos.author')}</th>
              <th>{$t('admin.banned_promos.created_at')}</th>
              <th>{$t('admin.banned_promos.verification_status')}</th>
              <th>{$t('admin.banned_promos.rejection_reason')}</th>
              <th>{$t('admin.banned_promos.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {#each promotions as promo (promo._id)}
              <tr>
                <td class="promo-name-cell">
                  <Link to={`/promotions/detail/${promo._id}`} class="promo-link">
                    {promo.name || 'Untitled Promotion'}
                  </Link>
                </td>
                <td class="author-cell">
                  {#if promo.createdByUserId}
                    <Link to={`/admin/users/${promo.createdByUserId}`} class="user-link">
                      {promo.createdByUserName || promo.createdByUserId}
                    </Link>
                  {:else}
                    <span class="text-muted">{promo.createdByUserName || $t('admin.banned_promos.unknown_author')}</span>
                  {/if}
                </td>
                <td class="date-cell">
                  <span class="datetime">{formatLocalDateTime(promo)}</span>
                </td>
                <td class="status-cell">
                  <span class="status-badge status-rejected">
                    {promo.verificationStatus || 'Rejected'}
                  </span>
                </td>
                <td class="reason-cell">
                  <span class="reason-text" title={promo.verificationReason || '-'}>
                    {promo.verificationReason || '-'}
                  </span>
                </td>
                <td class="action-cell">
                  <button
                    class="btn-unban"
                    on:click={() => handleUnban(promo)}
                    disabled={unbanningIds.has(promo._id)}
                  >
                    {#if unbanningIds.has(promo._id)}
                      {$t('admin.banned_promos.unbanning')}
                    {:else}
                      {$t('admin.banned_promos.unban_btn')}
                    {/if}
                  </button>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-wrapper">
        <div class="pagination-info">
          <span>{$t('common.page')} {page} {$t('common.of')} {totalPages}</span>
          <span class="total-badge">({totalCount})</span>
        </div>
        <div class="pagination-buttons">
          <button
            class="btn-page"
            disabled={page <= 1}
            on:click={() => goToPage(page - 1)}
          >
            &larr; {$t('common.prev')}
          </button>
          <button
            class="btn-page"
            disabled={page >= totalPages}
            on:click={() => goToPage(page + 1)}
          >
            {$t('common.next')} &rarr;
          </button>
        </div>
      </div>
    {/if}
  </div>
</div>

<style>
  .admin-container {
    max-width: 1200px;
    margin: 2rem auto;
    padding: 0 1rem;
    text-align: left;
  }

  .header-section {
    margin-bottom: 1.5rem;
  }

  h2 {
    margin: 0 0 0.5rem 0;
    font-size: 1.75rem;
    color: #222;
  }

  .subtitle {
    margin: 0;
    color: #666;
    font-size: 0.95rem;
  }

  .alert {
    padding: 0.85rem 1rem;
    border-radius: 6px;
    margin-bottom: 1.25rem;
    font-size: 0.95rem;
  }

  .alert-success {
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  }

  .alert-error {
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  }

  .table-card {
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    border: 1px solid #e9ecef;
    overflow: hidden;
  }

  .loading-state,
  .empty-state {
    padding: 3rem 1rem;
    text-align: center;
    color: #6c757d;
    font-size: 1.05rem;
  }

  .table-responsive {
    overflow-x: auto;
    width: 100%;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    min-width: 800px;
  }

  thead {
    background-color: #f8f9fa;
    border-bottom: 2px solid #dee2e6;
  }

  th {
    padding: 12px 16px;
    font-weight: 600;
    color: #495057;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    text-align: left;
  }

  td {
    padding: 14px 16px;
    border-bottom: 1px solid #f1f3f5;
    vertical-align: middle;
    font-size: 0.95rem;
    color: #333;
  }

  tbody tr:hover {
    background-color: #fcfcfd;
  }

  .promo-name-cell {
    max-width: 250px;
  }

  .promo-link {
    color: #007bff;
    text-decoration: none;
    font-weight: 600;
    word-break: break-word;
  }

  .promo-link:hover {
    text-decoration: underline;
    color: #0056b3;
  }

  .author-cell {
    max-width: 180px;
  }

  .user-link {
    color: #17a2b8;
    text-decoration: none;
    font-weight: 500;
    word-break: break-word;
  }

  .user-link:hover {
    text-decoration: underline;
    color: #117a8b;
  }

  .text-muted {
    color: #888;
  }

  .date-cell {
    white-space: nowrap;
    color: #555;
    font-size: 0.88rem;
  }

  .status-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .status-rejected {
    background-color: #ffeef0;
    color: #d73a49;
    border: 1px solid #fdb8c0;
  }

  .reason-cell {
    max-width: 260px;
  }

  .reason-text {
    display: block;
    color: #666;
    font-size: 0.88rem;
    word-break: break-word;
  }

  .action-cell {
    white-space: nowrap;
    text-align: right;
  }

  .btn-unban {
    padding: 6px 14px;
    background-color: #28a745;
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 0.88rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s, opacity 0.2s;
  }

  .btn-unban:hover:not(:disabled) {
    background-color: #218838;
  }

  .btn-unban:disabled {
    background-color: #6c757d;
    opacity: 0.65;
    cursor: not-allowed;
  }

  .pagination-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.25rem;
    background-color: #fdfdfd;
    border-top: 1px solid #eee;
    flex-wrap: wrap;
    gap: 1rem;
  }

  .pagination-info {
    font-size: 0.9rem;
    color: #666;
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }

  .total-badge {
    font-size: 0.85rem;
    color: #888;
  }

  .pagination-buttons {
    display: flex;
    gap: 0.5rem;
  }

  .btn-page {
    padding: 6px 14px;
    background-color: #fff;
    color: #007bff;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-page:hover:not(:disabled) {
    background-color: #007bff;
    color: #fff;
    border-color: #007bff;
  }

  .btn-page:disabled {
    background-color: #f8f9fa;
    color: #adb5bd;
    border-color: #dee2e6;
    cursor: not-allowed;
  }
</style>
